import { Component, computed, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { FilledButtonComponent } from '@custom-components/general/button';
import { PageEvent } from '@angular/material/paginator';
import { TranslateModule } from '@ngx-translate/core';
import { TableComponent } from '@custom-components/general/table/table.component';
import { TableFilterBarComponent } from '@custom-components/general/table-filter-bar/table-filter-bar.component';
import { SHIPMENTS_TABLE_COLUMNS_CONFIG } from '@core/constants';
import { TagStateOf } from '@core/helpers';
import { getUserRole, UserProfile } from '@core/models';
import { UserRoleEnum } from '@core/enums/user_role_enum';
import { ShipmentModel } from '@core/models/shipment.model';
import { ShipmentStore } from '@root/app/store/shipment_store';
import { UploadShipmentFileDialogComponent } from '../../components/upload-shipment-file-dialog/upload-shipment-file-dialog.component';
import { ShipmentFileDetailDialogComponent } from '../../components/shipment-file-detail-dialog/shipment-file-detail-dialog.component';

@Component({
  selector: 'xemex-shipment-files',
  imports: [
    FilledButtonComponent,
    MatIconModule,
    TranslateModule,
    TableComponent,
    TableFilterBarComponent,
  ],
  templateUrl: './shipment-files.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './shipment-files.component.scss',
})
export class ShipmentFilesComponent {
  readonly shipmentStore = inject(ShipmentStore);
  readonly dialog = inject(MatDialog);

  user = input<UserProfile | undefined>();

  readonly columns = SHIPMENTS_TABLE_COLUMNS_CONFIG;

  protected readonly tableData = computed(
    () =>
      (this.shipmentStore.shipmentData()?.shipments ?? []).map(s => ({
        ...s,
        statusState: TagStateOf.shipmentStatus(s.status),
      })) as unknown as Record<string, unknown>[],
  );
  protected readonly totalCount = computed(
    () => this.shipmentStore.shipmentData()?.numberOfItems ?? 0,
  );
  protected readonly isLoading = computed(() => this.shipmentStore.isLoadingShipments());

  constructor() {
    if (!this.shipmentStore.shipmentData()?.shipments?.length) {
      this.shipmentStore.getShipments(0);
    }
  }

  canAddShipment(): boolean {
    const u = this.user();
    if (!u) return false;
    const accessible: string[] = [
      UserRoleEnum.XemexAdmin,
      UserRoleEnum.XemexProduction,
      UserRoleEnum.XemexSupport,
    ];
    return accessible.includes(getUserRole(u)?.roleName ?? '');
  }

  protected onPageChange(evt: PageEvent): void {
    this.shipmentStore.getShipments(evt.pageIndex);
  }

  protected onUploadFiles(): void {
    this.dialog
      .open(UploadShipmentFileDialogComponent, {
        width: '850px',
        height: '544px',
        maxWidth: '850px',
      })
      .afterClosed()
      .subscribe(() => this.shipmentStore.getShipments(0));
  }

  protected onRowClick(row: Record<string, unknown>): void {
    this.dialog.open(ShipmentFileDetailDialogComponent, {
      width: '850px',
      maxWidth: '850px',
      maxHeight: '90vh',
      panelClass: 'h-full',
      data: {
        fileDetail: row as unknown as ShipmentModel,
        shipmentFileId: row['shipmentFileId'],
      },
    });
  }
}
